import type { FC } from "react";
import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { isAxiosError } from "axios";

import { useCreateConsultation } from "@/api/consultations";
import Calendar from "@/assets/icons/calendar";
import {
  FieldDatepicker,
  FieldInput,
  FieldMultipleCheckbox,
  FieldSelect,
  FieldTextArea,
} from "@/components/form";
import type { UpdateModalProps } from "@/components/modals/administrative-infos-update-modal";
import GenericModal from "@/components/modals/generic-modal";
import {
  createConsultationDefaultValues,
  createConsultationSchema,
} from "@/schemas/consultation";
import { consultationStepsOptions } from "@/types/api/consultation";
import { resetForm, stopPropagation } from "@/utils/form";
import {
  toothBrushHairTypeOptions,
  toothBrushTypeOptions,
} from "@/utils/patient";
import { mouthTypeOptions, sectorOptions } from "@/utils/treatment";

const CreateConsultationModal: FC<UpdateModalProps> = ({
  open,
  setOpen,
  patient,
}) => {
  const { mutate } = useCreateConsultation(patient.id);

  const { Field, handleSubmit, store, setFieldValue } = useForm({
    defaultValues: {
      ...createConsultationDefaultValues,
      patientId: patient.id,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = createConsultationSchema.parse(value);
      if (values.toothbrushTypes === null || !values.toothbrushTypes) {
        values.toothbrushTypes = [];
      }
      if (values.toothbrushHairTypes === null || !values.toothbrushHairTypes) {
        values.toothbrushHairTypes = [];
      }
      if (values.sectors === null || !values.sectors) {
        values.sectors = [];
      }
      if (values.mouthParts === null || !values.mouthParts) {
        values.mouthParts = [];
      }
      mutate(values, {
        onSuccess: () => {
          setOpen(false);
          resetForm(values, formApi);
        },
        onError: (e) => {
          if (isAxiosError(e)) {
            if (e.code === "ERR_NETWORK")
              return {
                fields: {
                  comment: "Erreur réseau, merci de réessayer ultérieurement",
                },
              };
          }
        },
      });
    },
  });

  const startDateValue = useStore(store, (state) => state.values.startDate);
  const consultationStepValue = useStore(
    store,
    (state) => state.values.consultationStep
  );

  useEffect(() => {
    if (!startDateValue) return;
    const newDate = new Date(startDateValue);
    newDate.setHours(newDate.getHours() + 1);
    setFieldValue("endDate", newDate);
  }, [startDateValue]);

  return (
    <GenericModal
      title="Ajouter une consultation"
      description="Complétez les informations de la consultation"
      submitLabel="Ajouter"
      logo={<Calendar className="w-6 h-6" color="black" />}
      open={open}
      setOpen={setOpen}
      handleSubmit={handleSubmit}
    >
      <form
        onSubmit={(e) => stopPropagation(e, handleSubmit)}
        className="grid grid-flow-row grid-cols-1 gap-6 px-6 w-full"
      >
        <FieldDatepicker
          Field={Field}
          name="startDate"
          label="Date de consultation"
          schema={createConsultationSchema}
        />
        <FieldSelect
          Field={Field}
          name="consultationStep"
          options={consultationStepsOptions}
          label="Motif"
          schema={createConsultationSchema}
        />
        <FieldTextArea
          Field={Field}
          name="comment"
          placeholder="Commentaires"
          schema={createConsultationSchema}
        />
        {consultationStepValue === "DEBRIDEMENT" ? (
          <div className="grid grid-flow-row grid-cols-1 w-full gap-4">
            <div className="text-sm font-medium text-muted-foreground">
              Secteurs
            </div>
            <FieldMultipleCheckbox
              Field={Field}
              options={sectorOptions}
              orientation="horizontal"
              name="sectors"
              schema={createConsultationSchema}
            />
            <div className="text-sm font-medium text-muted-foreground">
              Bouche
            </div>
            <FieldMultipleCheckbox
              Field={Field}
              options={mouthTypeOptions}
              orientation="horizontal"
              name="mouthParts"
              schema={createConsultationSchema}
            />
          </div>
        ) : null}
        <div className="text-md font-medium">Matériel prescrit</div>
        <FieldMultipleCheckbox
          Field={Field}
          options={toothBrushTypeOptions}
          label="Brosse à dents"
          orientation="horizontal"
          name="toothbrushTypes"
          schema={createConsultationSchema}
        />
        <FieldMultipleCheckbox
          Field={Field}
          options={toothBrushHairTypeOptions}
          orientation="horizontal"
          name="toothbrushHairTypes"
          schema={createConsultationSchema}
        />
        <FieldInput
          Field={Field}
          placeholder="Marque"
          name="toothbrushBrand"
          schema={createConsultationSchema}
        />
        <FieldInput
          Field={Field}
          label="Brossettes interdentaires"
          placeholder="Dimensions"
          name="brushesDimensions"
          schema={createConsultationSchema}
        />
        <FieldInput
          Field={Field}
          placeholder="Marque"
          name="brushesBrand"
          schema={createConsultationSchema}
        />
        <input type="submit" className="hidden" />
      </form>
    </GenericModal>
  );
};
export default CreateConsultationModal;
